// Weapons: knife and a USP pistol, with CS:S timings. Deterministic and tick-based like the rest of
// the simulation: everything comes in through the UserCmd (IN_ATTACK, IN_ATTACK2, IN_RELOAD,
// weaponSelect), and spread uses a seed from the tick count, so replays shoot the same.
//
// Knife: holding attack keeps swinging (slash every 0.4 s, stab every 1 s).
// Drop (G) throws away the weapon in hand; with both gone the hands are empty. !knife and !usp give
// them back. All of it comes through UserCmd.weaponSelect (see the SELECT_ codes), so replays match.
//
// USP (CS:S): 12 rounds, one shot per click, 0.15 s between shots, 2.7 s reload, starts reloading
// by itself when empty. Attack2 attaches/removes the silencer (it's on by default, like a USP-S).

import { EYE_DUCK, EYE_STAND, TICKRATE } from './constants';
import { MASK_SOLID, TraceResult, type CollisionWorld } from './collision/types';
import { IN_ATTACK, IN_ATTACK2, IN_RELOAD, type UserCmd } from './input';
import type { PlayerState } from './player';
import type { Vec3 } from './vec';

export type WeaponId = 'knife' | 'usp';

/** UserCmd.weaponSelect codes past switching (1 knife, 2 USP, 3 the other one). */
export const SELECT_DROP = 4;
export const SELECT_GIVE_KNIFE = 5;
export const SELECT_GIVE_USP = 6;
/**
 * 16..31: the whole loadout at once, instantly (bit 0 knife owned, bit 1 USP owned, bit 2 USP in
 * hand, bit 3 silenced). A run's first tick carries it, so a replay starts with what the player had.
 */
export const SELECT_LOADOUT = 16;

export const USP = {
  clip: 12,
  cycle: Math.round(0.15 * TICKRATE),
  reload: Math.round(2.7 * TICKRATE),
  deploy: Math.round(0.6 * TICKRATE),
  silencer: Math.round(2.4 * TICKRATE),
  range: 4096,
  damage: 34,
  damageSilenced: 30,
};

export const KNIFE_DEPLOY = Math.round(0.5 * TICKRATE);
export const KNIFE_SLASH = Math.round(0.4 * TICKRATE);
export const KNIFE_STAB = Math.round(1.0 * TICKRATE);

/** What happened this tick, for effects (cleared at the start of every tick). */
export interface ShotInfo {
  fired: boolean;
  hit: boolean;
  /** Impact point and surface normal (valid when hit). */
  pos: Vec3;
  normal: Vec3;
  /** Solid index hit (0 = world). */
  solid: number;
}

export class Weapons {
  /** In hand (null: both dropped, empty hands). */
  active: WeaponId | null = 'knife';
  hasKnife = true;
  hasUsp = true;
  ammo = USP.clip;
  silenced = true;
  /** Tick counters (ticks remaining). */
  busy = 0;
  reloading = 0;
  silencerChange = 0;
  deploying = 0;
  /** Events this tick for the client: bit flags. */
  events = 0;
  readonly shot: ShotInfo = { fired: false, hit: false, pos: [0, 0, 0], normal: [0, 0, 0], solid: -1 };
  /** Knife swing this tick: what the blade hit (`fired` = swung). */
  readonly knife: ShotInfo = { fired: false, hit: false, pos: [0, 0, 0], normal: [0, 0, 0], solid: -1 };
  private readonly tr = new TraceResult();
  private readonly eye: Vec3 = [0, 0, 0];
  private readonly end: Vec3 = [0, 0, 0];
  private shots = 0;
  /** Attack buttons held last tick (the USP fires once per press). */
  private lastButtons = 0;

  /** Back to a full magazine, keeping the weapon in hand (restart). */
  refill() {
    this.ammo = USP.clip;
    this.busy = this.reloading = this.silencerChange = this.deploying = 0;
  }

  /** The loadout as a SELECT_LOADOUT code (for the first tick of a run). */
  get loadoutCode() {
    return (
      SELECT_LOADOUT +
      (this.hasKnife ? 1 : 0) +
      (this.hasUsp ? 2 : 0) +
      (this.active === 'usp' ? 4 : 0) +
      (this.silenced ? 8 : 0)
    );
  }

  private owns(w: WeaponId) {
    return w === 'knife' ? this.hasKnife : this.hasUsp;
  }

  /** Put `w` in hand with its deploy time (a switch). */
  private draw(w: WeaponId) {
    this.active = w;
    this.reloading = this.silencerChange = 0;
    this.deploying = w === 'usp' ? USP.deploy : KNIFE_DEPLOY;
    this.busy = this.deploying;
    this.events |= WEV_SWITCHED;
  }

  reset() {
    this.active = 'knife';
    this.hasKnife = this.hasUsp = true;
    this.ammo = USP.clip;
    this.silenced = true;
    this.busy = this.reloading = this.silencerChange = this.deploying = 0;
    this.shots = 0;
    this.lastButtons = 0;
  }

  /**
   * Run one tick. Returns the solid hit by a bullet this tick (for damage), or -1.
   * `knifeAllowed` tells the caller whether knife attacks may happen this tick.
   */
  tick(cmd: Readonly<UserCmd>, p: PlayerState, world: CollisionWorld, tick: number): void {
    this.events = 0;
    const pressed = cmd.buttons & ~this.lastButtons;
    this.lastButtons = cmd.buttons & (IN_ATTACK | IN_ATTACK2);
    this.shot.fired = false;
    this.shot.hit = false;
    this.knife.fired = false;
    this.knife.hit = false;
    if (this.busy > 0) this.busy--;
    if (this.deploying > 0) this.deploying--;
    if (this.silencerChange > 0 && --this.silencerChange === 0) this.silenced = !this.silenced;
    if (this.reloading > 0 && --this.reloading === 0) {
      this.ammo = USP.clip;
      this.events |= WEV_RELOADED;
    }

    const sel = cmd.weaponSelect;
    if (sel >= SELECT_LOADOUT) {
      const b = sel - SELECT_LOADOUT;
      this.hasKnife = (b & 1) !== 0;
      this.hasUsp = (b & 2) !== 0;
      this.active =
        b & 4 ? (this.hasUsp ? 'usp' : null) : this.hasKnife ? 'knife' : this.hasUsp ? 'usp' : null;
      this.silenced = (b & 8) !== 0;
    } else if (sel === SELECT_DROP && this.active) {
      if (this.active === 'knife') this.hasKnife = false;
      else this.hasUsp = false;
      const next: WeaponId | null = this.hasKnife ? 'knife' : this.hasUsp ? 'usp' : null;
      this.events |= WEV_DROPPED;
      if (next) this.draw(next);
      else {
        this.active = null;
        this.reloading = this.silencerChange = this.deploying = this.busy = 0;
        this.events |= WEV_SWITCHED;
      }
      return;
    } else if (sel === SELECT_GIVE_KNIFE || sel === SELECT_GIVE_USP) {
      // Given: in hand at once (like picking it up in CS:S).
      const w: WeaponId = sel === SELECT_GIVE_KNIFE ? 'knife' : 'usp';
      if (w === 'knife') this.hasKnife = true;
      else this.hasUsp = true;
      if (w !== this.active) {
        this.draw(w);
        return;
      }
    } else {
      // Switching weapons cancels a reload or silencer change.
      const other: WeaponId = this.active === 'knife' ? 'usp' : 'knife';
      const want: WeaponId | null = sel === 1 ? 'knife' : sel === 2 ? 'usp' : sel === 3 ? other : null;
      if (want && want !== this.active && this.owns(want)) {
        this.draw(want);
        return;
      }
    }
    if (!this.active) return;
    if (this.active === 'knife') {
      // Held buttons keep swinging; slash wins if both are held.
      if (this.busy > 0 || this.deploying > 0) return;
      if (cmd.buttons & IN_ATTACK) {
        this.busy = KNIFE_SLASH;
        this.events |= WEV_SLASH;
        this.swing(cmd, p, world, 48);
      } else if (cmd.buttons & IN_ATTACK2) {
        this.busy = KNIFE_STAB;
        this.events |= WEV_STAB;
        this.swing(cmd, p, world, 32);
      }
      return;
    }
    if (this.deploying > 0 || this.silencerChange > 0) return;

    if (this.reloading > 0) return;
    const wantReload = (cmd.buttons & IN_RELOAD) !== 0 && this.ammo < USP.clip;
    if (wantReload || (this.ammo === 0 && this.busy === 0)) {
      this.reloading = USP.reload;
      this.events |= WEV_RELOAD;
      return;
    }
    if (this.busy > 0) return;
    if (pressed & IN_ATTACK2) {
      this.silencerChange = USP.silencer;
      this.busy = USP.silencer;
      this.events |= WEV_SILENCER;
      return;
    }
    if (pressed & IN_ATTACK && this.ammo > 0) this.fire(cmd, p, world, tick);
  }

  /** Knife reach: a line from the eye, then a head-sized hull if the line missed (like CS:S). */
  private swing(cmd: Readonly<UserCmd>, p: PlayerState, world: CollisionWorld, range: number) {
    const e = this.eye;
    e[0] = p.pos[0];
    e[1] = p.pos[1];
    e[2] = p.pos[2] + (p.ducked ? EYE_DUCK : EYE_STAND);
    const cp = Math.cos(cmd.pitch);
    this.end[0] = e[0] + Math.cos(cmd.yaw) * cp * range;
    this.end[1] = e[1] + Math.sin(cmd.yaw) * cp * range;
    this.end[2] = e[2] - Math.sin(cmd.pitch) * range;
    world.trace(e, this.end, ZERO, ZERO, MASK_SOLID, this.tr);
    if (this.tr.fraction >= 1)
      world.trace(e, this.end, KNIFE_HULL_MINS, KNIFE_HULL_MAXS, MASK_SOLID, this.tr);
    this.record(this.knife);
  }

  private record(s: ShotInfo) {
    s.fired = true;
    s.hit = this.tr.fraction < 1;
    if (s.hit) {
      s.pos[0] = this.tr.endPos[0];
      s.pos[1] = this.tr.endPos[1];
      s.pos[2] = this.tr.endPos[2];
      s.normal[0] = this.tr.planeNormal[0];
      s.normal[1] = this.tr.planeNormal[1];
      s.normal[2] = this.tr.planeNormal[2];
      s.solid = this.tr.entity?.solidIndex ?? 0;
    } else s.solid = -1;
  }

  private fire(cmd: Readonly<UserCmd>, p: PlayerState, world: CollisionWorld, tick: number) {
    this.ammo--;
    this.busy = USP.cycle;
    this.shots++;
    this.events |= WEV_FIRED;
    // Spread: small standing still, larger moving, much larger in the air (CS:S style).
    const speed = Math.hypot(p.vel[0], p.vel[1]);
    const spread = !p.ground ? 0.06 : speed > 140 ? 0.02 : p.ducked ? 0.004 : 0.006;
    // Deterministic pseudo-random spread from the tick and shot count.
    let h = (tick * 2654435761 + this.shots * 40503) >>> 0;
    const rnd = () => {
      h = (h ^ (h << 13)) >>> 0;
      h = (h ^ (h >>> 17)) >>> 0;
      h = (h ^ (h << 5)) >>> 0;
      return (h / 4294967296) * 2 - 1;
    };
    const yaw = cmd.yaw + rnd() * spread;
    const pitch = cmd.pitch + rnd() * spread;
    const e = this.eye;
    e[0] = p.pos[0];
    e[1] = p.pos[1];
    e[2] = p.pos[2] + (p.ducked ? EYE_DUCK : EYE_STAND);
    const cp = Math.cos(pitch);
    this.end[0] = e[0] + Math.cos(yaw) * cp * USP.range;
    this.end[1] = e[1] + Math.sin(yaw) * cp * USP.range;
    this.end[2] = e[2] - Math.sin(pitch) * USP.range;
    world.trace(e, this.end, ZERO, ZERO, MASK_SOLID, this.tr);
    this.record(this.shot);
  }

  get damage() {
    return this.silenced ? USP.damageSilenced : USP.damage;
  }
}

const ZERO: Vec3 = [0, 0, 0];
const KNIFE_HULL_MINS: Vec3 = [-16, -16, -18];
const KNIFE_HULL_MAXS: Vec3 = [16, 16, 18];

export const WEV_FIRED = 1;
export const WEV_RELOAD = 2;
export const WEV_RELOADED = 4;
export const WEV_SWITCHED = 8;
export const WEV_SILENCER = 16;
/** Knife swings. */
export const WEV_SLASH = 32;
export const WEV_STAB = 64;
/** The weapon in hand was dropped (with WEV_SWITCHED: to the other one, or to empty hands). */
export const WEV_DROPPED = 128;
